/**
 * Staggered reveal for agent-written sequencer cells (SPEC §6 item 5).
 *
 * `revealCells(steps)` gives each step's cell(s) a `--i` index and a
 * `data-reveal` attribute; a CSS keyframe (opacity + scale from .6) plays with
 * a 20 ms per-step stagger (cells on the same step — a chord — share one
 * index and appear together). Long runs compress the stagger so the whole
 * cascade stays within 700 ms. Under `prefers-reduced-motion` nothing animates: the cells
 * simply appear (the caller has already flipped their `data-on`).
 *
 * The stylesheet is injected once; `data-reveal` is removed on `animationend`
 * or, as a fallback (hidden tabs, jsdom), after the total budget elapses.
 */
import { injectStyle, reducedMotion } from './dom';

/** Per-step stagger delay in ms. */
export const REVEAL_STEP_MS = 20;
/** Duration of the per-cell keyframe in ms. */
export const REVEAL_DURATION_MS = 220;
/** Upper bound for the whole cascade (last delay + duration) in ms. */
export const REVEAL_MAX_MS = 700;

export interface RevealOptions {
  /** Per-step delay in ms (default 20). */
  stepMs?: number;
  /** Total budget in ms (default 700); the stagger compresses to stay inside it. */
  maxMs?: number;
}

const STYLE_ID = 'bm-reveal-style';
const CSS = `
@keyframes bm-reveal {
  from { opacity: .15; transform: scale(.6); }
  to   { opacity: 1;   transform: scale(1); }
}
[data-reveal] {
  animation: bm-reveal ${REVEAL_DURATION_MS}ms cubic-bezier(.2,.8,.3,1.2) both;
  animation-delay: calc(var(--i, 0) * var(--reveal-step, ${REVEAL_STEP_MS}ms));
}
@media (prefers-reduced-motion: reduce) {
  [data-reveal] { animation: none; }
}
`;

/**
 * Stagger delay to use for `count` cells so `(count - 1) * step + duration <= maxMs`.
 * Returns the nominal step when the run fits.
 */
export function revealStepMs(count: number, opts: RevealOptions = {}): number {
  const step = opts.stepMs ?? REVEAL_STEP_MS;
  const max = opts.maxMs ?? REVEAL_MAX_MS;
  if (count <= 1) return step;
  const budget = Math.max(0, max - REVEAL_DURATION_MS);
  return Math.min(step, budget / (count - 1));
}

const pending = new WeakMap<HTMLElement, { timer: number; onEnd: () => void }>();

function clear(el: HTMLElement): void {
  const p = pending.get(el);
  if (p) {
    clearTimeout(p.timer);
    el.removeEventListener('animationend', p.onEnd);
    pending.delete(el);
  }
  el.removeAttribute('data-reveal');
}

/**
 * One reveal step: a single cell, or several cells that should appear together
 * (e.g. the three notes of a chord on the same step share one `--i`).
 */
export type RevealStep = HTMLElement | HTMLElement[];

/**
 * Reveal `steps` in array order with a staggered keyframe; every cell inside
 * one step gets the same `--i`, so the stagger is per STEP, not per cell.
 * Safe to call again while a previous reveal is in flight (it restarts).
 * Instant under reduced motion.
 */
export function revealCells(steps: RevealStep[], opts: RevealOptions = {}): void {
  const groups = steps.map((s) => (Array.isArray(s) ? s : [s])).filter((g) => g.length > 0);
  if (!groups.length) return;
  if (reducedMotion()) {
    for (const g of groups) for (const el of g) clear(el);
    return;
  }
  injectStyle(STYLE_ID, CSS);
  const step = revealStepMs(groups.length, opts);
  const max = opts.maxMs ?? REVEAL_MAX_MS;
  const stepText = `${Math.round(step * 100) / 100}ms`;
  groups.forEach((group, i) => {
    for (const el of group) {
      clear(el);
      el.style.setProperty('--i', String(i));
      el.style.setProperty('--reveal-step', stepText);
      // Restart the animation when the attribute was just removed.
      void el.offsetWidth;
      el.setAttribute('data-reveal', '');
      const onEnd = () => clear(el);
      el.addEventListener('animationend', onEnd, { once: true });
      const timer = window.setTimeout(onEnd, max + 50);
      pending.set(el, { timer, onEnd });
    }
  });
}
